import { useEffect, useState } from "react";
import {
  CONTROLLER_PAIRING_STEPS,
  OTHER_CONTROLLERS_FAQ_URL,
} from "../../../constants/controllerInstruction";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { CONSOLE_VIDEO_ID } from "../../../constants/videos";
import { useGuideVideo } from "../../../hooks/useGuideVideo";
import { YtbVideoPlayer } from "../YtbVideoPlayer/YtbVideoPlayer";
import { VideoPopupLayout } from "../VideoPopupLayout";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { Button } from "../../core/Button/Button";
import { isTV } from "../../../utils/platform";
import { useStyles } from "./MobileLayout.style";
import StepImage from "./StepImage";

type TMobileLayout = {
  open: boolean;
  onClose?: () => void;
  videoError: boolean;
  setVideoError: (error: boolean) => void;
};

const MobileLayout: React.FC<TMobileLayout> = (props) => {
  const { open, onClose } = props;
  const classes = useStyles();
  const consoleVideoUrl = useGuideVideo(CONSOLE_VIDEO_ID);
  const [videoError, setVideoError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const isLast = activeStep === CONTROLLER_PAIRING_STEPS.length - 1;
  const step = CONTROLLER_PAIRING_STEPS[activeStep];

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
    }
  }, [open]);

  const handleConnectionGuideClick = () => {
    window.open(OTHER_CONTROLLERS_FAQ_URL, "_blank", "noopener,noreferrer");
  };

  // Layout TV
  if (isTV) {
    return (
      <VideoPopupLayout
        open={open}
        onClose={onClose}
        videoUrl={consoleVideoUrl}
        videoTitle="Controller Connection Guide"
        onVideoError={() => setVideoError(true)}
        title="Connect ONMO+ Pro Controller"
        description={
          <Typography
            variant={TypographyVariant.BodyB3Medium}
            className={classes.descLarge}
          >
            Click for more help on additional controller features and guides.
          </Typography>
        }
        actions={
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              id="close-dialog"
              data-button-sound="back"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConnectionGuideClick}
              id="pair-controller-mobile-action"
              data-gamepad="default-selected"
            >
              Connection Guide
            </Button>
          </>
        }
      />
    );
  }

  return (
    <Dialog
      open={open}
      className={classes.mobileContent}
      mainAction={{
        text: "Connection Guide",
        onClick: handleConnectionGuideClick,
        id: isLast ? "close-dialog" : "pair-controller-mobile-action",
        size: "large",
      }}
      secondAction={{
        text: "Cancel",
        onClick: onClose,
        id: "close-dialog",
        "data-button-sound": "back",
        size: "large",
      }}
    >
      <Typography
        variant={TypographyVariant.BodyB2Bold}
        color={TypographyColor.Default}
        className={classes.mobileTitle}
      >
        Connect ONMO+ Pro Controller
      </Typography>

      <div className={classes.mobileSlide}>
        {!videoError && consoleVideoUrl && (
          <div className={classes.mobileVideoArea}>
            <YtbVideoPlayer
              url={consoleVideoUrl}
              loop
              title="Controller Connection Guide"
              className={classes.videoIframe}
              onError={() => setVideoError(true)}
            />
          </div>
        )}

        {videoError && (
          <>
            <div className={classes.mobileImageArea}>
              <StepImage src={step.image} size={160} />
            </div>
            <div className={classes.mobileTextArea}>
              <Typography
                variant={TypographyVariant.BodyB5Bold}
                color={TypographyColor.Default}
              >
                {step.title}
              </Typography>
              <Typography
                variant={TypographyVariant.BodyB5Medium}
                color={TypographyColor.Gray}
              >
                {step.desc}
              </Typography>
            </div>
            <div className={classes.dotRow}>
              {CONTROLLER_PAIRING_STEPS.map((_, i) => (
                <button
                  key={i}
                  className={`${classes.dot} ${
                    i === activeStep ? classes.dotActive : ""
                  }`}
                  onClick={() => setActiveStep(i)}
                  data-gamepad={`slide-dot-${i}`}
                  data-selected-class={classes.selected}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default MobileLayout;
